import React from "react";
import { useToast } from "@/hooks/use-toast";
import { usePlatformContact } from "../../hooks/usePlatformContact";
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
  ToastDestructiveIcon,
} from "@/components/ui/toast";

// Matches any email inside a plain-text toast description so we can turn
// it into a clickable mailto link with high-contrast styling.
const EMAIL_RX = /([\w.+-]+@[\w-]+\.[\w.-]+)/g;
// The bundled fallback address written into friendlyError.js. When we see
// this exact address in a description, we swap it for the clinic's
// admin-configured support email (from `usePlatformContact`) so the user
// contacts the RIGHT inbox.
const FALLBACK_SUPPORT_EMAIL = "support@myhometowndirect.com";

/**
 * Render a plain-text description with:
 *   1. Any emails swapped to <a mailto> links (destructive variant styles
 *      the link white/underlined so it pops against the red background).
 *   2. The bundled fallback support email replaced with the admin-managed
 *      one (single source of truth = App Settings → Support Contact).
 */
function renderDescription(text, supportEmail, isDestructive) {
  if (typeof text !== "string" || !text) return text;

  const normalised = supportEmail
    ? text.split(FALLBACK_SUPPORT_EMAIL).join(supportEmail)
    : text;

  const parts = normalised.split(EMAIL_RX);
  const linkClass = isDestructive
    ? "font-bold underline underline-offset-2 decoration-2 hover:text-white/90 break-all"
    : "font-semibold underline underline-offset-2 text-red-700 hover:text-red-900 break-all";

  return parts.map((part, i) => {
    if (EMAIL_RX.test(part)) {
      // Reset lastIndex — /g regex state is sticky between calls.
      EMAIL_RX.lastIndex = 0;
      return (
        <a
          key={i}
          href={`mailto:${part}`}
          className={linkClass}
          data-testid="toast-support-mailto"
        >
          {part}
        </a>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

export function Toaster() {
  const { toasts } = useToast();
  const { email: supportEmail } = usePlatformContact();

  return (
    <ProviderThemeSafe>
      {toasts.map(function ({ id, title, description, action, variant, ...props }) {
        const isDestructive = variant === "destructive";
        return (
          <Toast key={id} variant={variant} {...props}>
            {isDestructive && (
              <div className="flex-shrink-0 pt-0.5" aria-hidden="true">
                <div className="rounded-full bg-white/20 p-1.5">
                  <ToastDestructiveIcon className="h-5 w-5 text-white" />
                </div>
              </div>
            )}
            <div className="grid gap-1 flex-1 min-w-0">
              {title && <ToastTitle>{title}</ToastTitle>}
              {description && (
                <ToastDescription>
                  {renderDescription(description, supportEmail, isDestructive)}
                </ToastDescription>
              )}
            </div>
            {action}
            <ToastClose />
          </Toast>
        );
      })}
      <ToastViewport />
    </ProviderThemeSafe>
  );
}

// Small local wrapper so we can render children only once ToastProvider
// context is mounted. Keeps this file the only place that imports
// ToastProvider from Radix, matching the previous behaviour.
function ProviderThemeSafe({ children }) {
  return <ToastProvider>{children}</ToastProvider>;
}
